/**
 * Draft recording persistence.
 *
 * The active manifest stays in {@code localStorage} so reads remain synchronous across the recording state
 * machine. Every draft is also kept in a map keyed by recording id, so pausing one visit and starting another
 * does not erase the first. The large audio slices are stored as native {@link Blob}s in IndexedDB, keyed by
 * recording id, which has a far higher quota than localStorage, avoids ~33% base64 overhead, never blocks the
 * main thread, and behaves consistently in mobile WebViews (iOS WKWebView / Android).
 */

/** Manifest for the recording currently open on this page. */
export const RECORDING_CHECKPOINT_STORAGE_KEY = 'scryber_recording_checkpoint';

/** All drafts still resumable in this browser, keyed by lowercase recording id. */
const PAUSED_DRAFTS_STORAGE_KEY = 'scryber_paused_recording_drafts';

/** Stable identity for one user recording attempt (from Start until transcribe success or abandon). */
export interface RecordingCheckpointState {
  recordingId: string;
  recordingStartedAt: number;
  lastUploadAt: number;
  /** Highest chunk index written for this recording (inclusive), or -1 if none. */
  lastChunkIndex: number;
  /** Total seconds for the visit timer (carried across pause/resume in the same browser). */
  accumulatedDurationSeconds?: number;
  /** Paused transcription row id while the draft is still resumable. */
  pausedTranscriptionDbId?: number;
  /** Once a real patient name was chosen, do not show the pause name dialog again for this draft. */
  visitPatientNameLocked?: boolean;
  /** Reuse this patient when skipping name on Unknown visits (no duplicate Unknown rows). */
  draftPatientId?: number;
  /**
   * Next 1-based S3 checkpoint filename index ({@code chunks-{n}.{ext}}). Monotonic across pause/resume;
   * must not be derived from {@link #lastChunkIndex} alone (partial batches reuse chunk ranges).
   */
  nextCheckpointBatchNumber?: number;
  /** Highest chunk index (inclusive) confirmed on S3 via a successful checkpoint POST — not the same as {@link #lastChunkIndex}. */
  lastUploadedChunkIndex?: number;
  /**
   * Recorder mime type chosen when the draft started (e.g. {@code audio/webm;codecs=opus} or {@code audio/mp4}).
   * Persisted so a resumed take on the same device reuses the same container and slices stitch cleanly.
   */
  recorderMimeType?: string;
}

/** Always RFC-4122-shaped so server routes (S3 prefix, {@code UUID.fromString}) stay valid. */
export function createRecordingUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// IndexedDB chunk store
// ---------------------------------------------------------------------------

const DB_NAME = 'scryber-recordings';
/** v2: string {@code id} key + {@link ArrayBuffer} payload (Safari is unreliable with compound keys and Blob values). */
const DB_VERSION = 2;
const CHUNK_STORE = 'chunks';
/** chunkIndex is always >= 0; this ceiling lets a single key-range delete cover an entire recording. */
const MAX_CHUNK_INDEX = Number.MAX_SAFE_INTEGER;

interface ChunkRecord {
  /** Sortable string key: {@code {recordingId}:{zeroPaddedIndex}} — avoids Safari compound-key bugs. */
  id: string;
  recordingId: string;
  chunkIndex: number;
  mimeType: string;
  /** Stored as ArrayBuffer for Safari IndexedDB reliability; rehydrated to {@link Blob} on read. */
  data: ArrayBuffer;
}

/** Legacy v1 used compound keyPath; kept so {@code onupgradeneeded} can drop the old store. */
const LEGACY_CHUNK_STORE = 'chunks';

function chunkRecordId(recordingId: string, chunkIndex: number): string {
  return `${recordingId}:${String(chunkIndex).padStart(8, '0')}`;
}

function idbAvailable(): boolean {
  if (typeof indexedDB === 'undefined') {
    return false;
  }
  try {
    if (typeof indexedDB.open !== 'function') {
      return false;
    }
    // Safari private browsing exposes indexedDB but rejects all operations.
    const probe = indexedDB.open('scryber-idb-probe');
    probe.onerror = () => undefined;
    probe.onsuccess = () => probe.result.close();
    return true;
  } catch {
    return false;
  }
}

type ChunkBackend = 'indexeddb' | 'localstorage';

let storageBackend: ChunkBackend | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(LEGACY_CHUNK_STORE)) {
        db.deleteObjectStore(LEGACY_CHUNK_STORE);
      }
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        db.createObjectStore(CHUNK_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

/** Open IndexedDB early; falls back to localStorage when Safari blocks or rejects IndexedDB. */
export function warmUpRecordingChunkStorage(): Promise<ChunkBackend> {
  if (storageBackend) {
    return Promise.resolve(storageBackend);
  }
  if (!idbAvailable()) {
    storageBackend = 'localstorage';
    console.warn('[recording-storage] IndexedDB unavailable; using localStorage fallback for audio slices');
    return Promise.resolve(storageBackend);
  }
  return openDb()
    .then(async (db) => {
      const probeId = '__storage_probe__';
      const probe: ChunkRecord = {
        id: chunkRecordId(probeId, 0),
        recordingId: probeId,
        chunkIndex: 0,
        mimeType: 'audio/webm',
        data: new ArrayBuffer(0),
      };
      await putChunkRecordOnDb(db, probe);
      await deleteChunkRangeOnDb(db, probeId, 0, 0);
      storageBackend = 'indexeddb';
      return storageBackend;
    })
    .catch((err) => {
      console.warn('[recording-storage] IndexedDB probe failed; using localStorage fallback', err);
      storageBackend = 'localstorage';
      return storageBackend;
    });
}

function ensureBackend(): Promise<ChunkBackend> {
  if (storageBackend) {
    return Promise.resolve(storageBackend);
  }
  return warmUpRecordingChunkStorage();
}

/**
 * All chunk reads/writes/deletes run through one promise chain so they execute in call order regardless of
 * await timing. This guarantees, e.g., that a "delete slices from index N" issued on cancel completes before a
 * later resume writes new slices at index N for the same recording id.
 */
let opQueue: Promise<unknown> = Promise.resolve();

function enqueue<T>(op: () => Promise<T>): Promise<T> {
  const run = opQueue.then(op, op);
  opQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

function putChunkRecordOnDb(db: IDBDatabase, record: ChunkRecord): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    tx.objectStore(CHUNK_STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function putChunkRecord(record: ChunkRecord): Promise<void> {
  return openDb().then((db) => putChunkRecordOnDb(db, record));
}

function getChunkRecord(recordingId: string, chunkIndex: number): Promise<ChunkRecord | null> {
  const id = chunkRecordId(recordingId, chunkIndex);
  return openDb().then(
    (db) =>
      new Promise<ChunkRecord | null>((resolve, reject) => {
        const tx = db.transaction(CHUNK_STORE, 'readonly');
        const req = tx.objectStore(CHUNK_STORE).get(id);
        req.onsuccess = () => resolve((req.result as ChunkRecord | undefined) ?? null);
        req.onerror = () => reject(req.error);
      })
  );
}

function deleteChunkRangeOnDb(
  db: IDBDatabase,
  recordingId: string,
  fromIndex: number,
  toIndexInclusive: number
): Promise<void> {
  if (fromIndex > toIndexInclusive) {
    return Promise.resolve();
  }
  const lo = chunkRecordId(recordingId, fromIndex);
  const hi = chunkRecordId(recordingId, toIndexInclusive);
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(CHUNK_STORE, 'readwrite');
    const range = IDBKeyRange.bound(lo, hi);
    tx.objectStore(CHUNK_STORE).delete(range);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function deleteChunkRange(recordingId: string, fromIndex: number, toIndexInclusive: number): Promise<void> {
  if (fromIndex > toIndexInclusive) {
    return Promise.resolve();
  }
  return openDb().then((db) => deleteChunkRangeOnDb(db, recordingId, fromIndex, toIndexInclusive));
}

// ---------------------------------------------------------------------------
// localStorage fallback (base64) when IndexedDB is blocked — same key shape as pre-IndexedDB code
// ---------------------------------------------------------------------------

function legacyChunkLocalKey(recordingId: string, chunkIndex: number): string {
  return `${recordingId}-${chunkIndex}`;
}

interface LegacyChunkPayload {
  mimeType: string;
  dataBase64: string;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const s = reader.result?.toString() ?? '';
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(dataBase64: string, mimeType: string): Blob {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

function saveChunkLocalStorage(recordingId: string, chunkIndex: number, mimeType: string, blob: Blob): Promise<void> {
  return blobToBase64(blob).then((dataBase64) => {
    const payload: LegacyChunkPayload = { mimeType, dataBase64 };
    localStorage.setItem(legacyChunkLocalKey(recordingId, chunkIndex), JSON.stringify(payload));
  });
}

function loadChunkLocalStorage(recordingId: string, chunkIndex: number): Blob | null {
  const raw = localStorage.getItem(legacyChunkLocalKey(recordingId, chunkIndex));
  if (!raw) {
    return null;
  }
  try {
    const payload = JSON.parse(raw) as LegacyChunkPayload;
    if (!payload?.dataBase64) {
      return null;
    }
    return base64ToBlob(payload.dataBase64, payload.mimeType || 'audio/webm;codecs=opus');
  } catch {
    return null;
  }
}

function removeChunkLocalStorageRange(recordingId: string, fromIndex: number, toIndexInclusive: number): void {
  for (let i = fromIndex; i <= toIndexInclusive; i++) {
    try {
      localStorage.removeItem(legacyChunkLocalKey(recordingId, i));
    } catch {
      // ignore
    }
  }
}

function removeChunkLocalStorageFromIndex(recordingId: string, fromIndex: number): void {
  if (fromIndex < 0) {
    return;
  }
  const prefix = `${recordingId}-`;
  const keys: string[] = [];
  try {
    for (let k = 0; k < localStorage.length; k++) {
      const key = localStorage.key(k);
      if (!key || !key.startsWith(prefix)) {
        continue;
      }
      const tail = key.slice(prefix.length);
      if (/^\d+$/.test(tail) && parseInt(tail, 10) >= fromIndex) {
        keys.push(key);
      }
    }
  } catch {
    return;
  }
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

function removeAllChunkLocalStorageForRecording(recordingId: string): void {
  removeChunkLocalStorageFromIndex(recordingId, 0);
}

async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return blob.arrayBuffer();
}

// ---------------------------------------------------------------------------
// Manifest (localStorage)
// ---------------------------------------------------------------------------

function parseManifest(raw: string): RecordingCheckpointState | null {
  try {
    const v = JSON.parse(raw) as RecordingCheckpointState & {
      sessionId?: string;
      stitchedPreview?: unknown;
    };
    if (v.recordingId) {
      return {
        recordingId: v.recordingId,
        recordingStartedAt:
          typeof v.recordingStartedAt === 'number'
            ? v.recordingStartedAt
            : typeof v.lastUploadAt === 'number'
              ? v.lastUploadAt
              : Date.now(),
        lastUploadAt: typeof v.lastUploadAt === 'number' ? v.lastUploadAt : Date.now(),
        lastChunkIndex: typeof v.lastChunkIndex === 'number' ? v.lastChunkIndex : -1,
        accumulatedDurationSeconds:
          typeof v.accumulatedDurationSeconds === 'number' ? v.accumulatedDurationSeconds : undefined,
        pausedTranscriptionDbId:
          typeof v.pausedTranscriptionDbId === 'number' ? v.pausedTranscriptionDbId : undefined,
        visitPatientNameLocked: typeof v.visitPatientNameLocked === 'boolean' ? v.visitPatientNameLocked : undefined,
        draftPatientId: typeof v.draftPatientId === 'number' ? v.draftPatientId : undefined,
        nextCheckpointBatchNumber:
          typeof v.nextCheckpointBatchNumber === 'number' && v.nextCheckpointBatchNumber >= 1
            ? v.nextCheckpointBatchNumber
            : undefined,
        lastUploadedChunkIndex:
          typeof v.lastUploadedChunkIndex === 'number' ? v.lastUploadedChunkIndex : undefined,
        recorderMimeType: typeof v.recorderMimeType === 'string' && v.recorderMimeType ? v.recorderMimeType : undefined,
      };
    }
    if (v.sessionId) {
      return {
        recordingId: v.sessionId,
        recordingStartedAt: typeof v.lastUploadAt === 'number' ? v.lastUploadAt : Date.now(),
        lastUploadAt: typeof v.lastUploadAt === 'number' ? v.lastUploadAt : Date.now(),
        lastChunkIndex: -1,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function readRecordingCheckpoint(): RecordingCheckpointState | null {
  try {
    const raw = localStorage.getItem(RECORDING_CHECKPOINT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return parseManifest(raw);
  } catch {
    return null;
  }
}

/** Active draft, or a paused draft kept after another visit became active. */
export function readRecordingCheckpointById(recordingId: string): RecordingCheckpointState | null {
  const id = recordingId.trim().toLowerCase();
  if (!id) {
    return null;
  }
  const active = readRecordingCheckpoint();
  if (active?.recordingId.toLowerCase() === id) {
    return active;
  }
  return readDraftMap()[id] ?? null;
}

function readDraftMap(): Record<string, RecordingCheckpointState> {
  try {
    const raw = localStorage.getItem(PAUSED_DRAFTS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, RecordingCheckpointState> = {};
    for (const value of Object.values(parsed as Record<string, unknown>)) {
      const state = parseManifest(JSON.stringify(value));
      if (state?.recordingId) {
        out[state.recordingId.toLowerCase()] = state;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeDraftMap(map: Record<string, RecordingCheckpointState>): void {
  try {
    localStorage.setItem(PAUSED_DRAFTS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function upsertDraft(state: RecordingCheckpointState): void {
  const map = readDraftMap();
  map[state.recordingId.toLowerCase()] = state;
  writeDraftMap(map);
}

function removeDraft(recordingId: string): void {
  const id = recordingId.trim().toLowerCase();
  if (!id) {
    return;
  }
  const map = readDraftMap();
  if (!(id in map)) {
    return;
  }
  delete map[id];
  writeDraftMap(map);
}

/** Make {@code state} the open recording and keep a copy in the draft map. */
function writeActiveManifest(state: RecordingCheckpointState): void {
  try {
    localStorage.setItem(RECORDING_CHECKPOINT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
  upsertDraft(state);
}

/**
 * Update a draft in the map. The active key changes only when it is already this recording, so a late save
 * for a paused visit does not replace the visit now being recorded.
 */
function writeManifest(state: RecordingCheckpointState): void {
  const active = readRecordingCheckpoint();
  if (!active || active.recordingId.toLowerCase() === state.recordingId.toLowerCase()) {
    try {
      localStorage.setItem(RECORDING_CHECKPOINT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }
  upsertDraft(state);
}

/** Drop the active pointer. Chunks and the draft map entry stay so a paused visit can be resumed. */
function detachActiveCheckpoint(): void {
  try {
    localStorage.removeItem(RECORDING_CHECKPOINT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Starting a new visit. A paused draft stays resumable. An unfinished take that was never paused is removed.
 */
export function releaseActiveCheckpointForNewVisit(): void {
  const prev = readRecordingCheckpoint();
  if (!prev) {
    return;
  }
  if (prev.pausedTranscriptionDbId != null) {
    upsertDraft(prev);
    detachActiveCheckpoint();
    return;
  }
  clearRecordingCheckpoint();
}

// ---------------------------------------------------------------------------
// Chunk deletion helpers (manifest stays in sync; IndexedDB work is serialized best-effort)
// ---------------------------------------------------------------------------

export function removeChunkKeysRange(recordingId: string, fromIndex: number, toIndexInclusive: number): void {
  void removeChunkKeysRangeAsync(recordingId, fromIndex, toIndexInclusive).catch(() => undefined);
}

/** Awaitable variant — callers must await before advancing upload cursors so IDB deletes are not re-written by a late persist. */
export function removeChunkKeysRangeAsync(
  recordingId: string,
  fromIndex: number,
  toIndexInclusive: number
): Promise<void> {
  if (fromIndex > toIndexInclusive) {
    return Promise.resolve();
  }
  return ensureBackend().then((b) => {
    if (b === 'localstorage') {
      removeChunkLocalStorageRange(recordingId, fromIndex, toIndexInclusive);
      return undefined;
    }
    return enqueue(() => deleteChunkRange(recordingId, fromIndex, toIndexInclusive));
  });
}

/**
 * Remove all slices for this recording with index {@code >= fromIndex}. Does not change the manifest;
 * callers typically {@link restoreRecordingCheckpointManifest} afterward.
 */
export function removeChunkKeysFromIndex(recordingId: string, fromIndex: number): void {
  if (fromIndex < 0) {
    return;
  }
  void ensureBackend().then((b) => {
    if (b === 'localstorage') {
      removeChunkLocalStorageFromIndex(recordingId, fromIndex);
      return;
    }
    void enqueue(() => deleteChunkRange(recordingId, fromIndex, MAX_CHUNK_INDEX)).catch(() => undefined);
  });
}

/** Remove the active manifest, its draft-map entry, and all chunk data for that recording. */
export function clearRecordingCheckpoint(): void {
  const prev = readRecordingCheckpoint();
  detachActiveCheckpoint();
  if (!prev) {
    return;
  }
  removeDraft(prev.recordingId);
  void ensureBackend().then((b) => {
    if (b === 'localstorage') {
      removeAllChunkLocalStorageForRecording(prev.recordingId);
      return;
    }
    void enqueue(() => deleteChunkRange(prev.recordingId, 0, MAX_CHUNK_INDEX)).catch(() => undefined);
  });
}

/** Remove manifest and chunk data for a known recording (e.g. after successful transcribe). */
export function clearRecordingDraftForId(recordingId: string): void {
  const manifest = readRecordingCheckpoint();
  if (manifest?.recordingId.toLowerCase() === recordingId.trim().toLowerCase()) {
    detachActiveCheckpoint();
  }
  removeDraft(recordingId);
  void ensureBackend().then((b) => {
    if (b === 'localstorage') {
      removeAllChunkLocalStorageForRecording(recordingId);
      return;
    }
    void enqueue(() => deleteChunkRange(recordingId, 0, MAX_CHUNK_INDEX)).catch(() => undefined);
  });
}

/** Draft id embedded in a paused transcription row (`paused-recording:{uuid}`). */
export function parsePausedRecordingDraftId(originalFileName: string | null | undefined): string | null {
  const m = (originalFileName ?? '').trim().match(/^paused-recording:(.+)$/i);
  const id = m?.[1]?.trim();
  return id || null;
}

/**
 * After the user deletes a paused transcription, drop matching local draft data.
 * If the active checkpoint manifest is for this recording, clears it and its chunks; otherwise only removes
 * the chunk data for this id so another in-progress draft manifest is left intact.
 */
export function clearLocalStorageForPausedTranscriptionDraft(originalFileName: string | null | undefined): void {
  const recordingId = parsePausedRecordingDraftId(originalFileName);
  if (!recordingId) {
    return;
  }
  const manifest = readRecordingCheckpoint();
  if (manifest?.recordingId.toLowerCase() === recordingId.toLowerCase()) {
    clearRecordingCheckpoint();
    return;
  }
  removeDraft(recordingId);
  void ensureBackend().then((b) => {
    if (b === 'localstorage') {
      removeAllChunkLocalStorageForRecording(recordingId);
      return;
    }
    void enqueue(() => deleteChunkRange(recordingId, 0, MAX_CHUNK_INDEX)).catch(() => undefined);
  });
}

// ---------------------------------------------------------------------------
// Manifest writers
// ---------------------------------------------------------------------------

/** Overwrite the active manifest with a known snapshot (used when restoring a paused draft manifest). */
export function restoreRecordingCheckpointManifest(state: RecordingCheckpointState): void {
  writeActiveManifest({
    recordingId: state.recordingId,
    recordingStartedAt: state.recordingStartedAt,
    lastUploadAt: state.lastUploadAt,
    lastChunkIndex: state.lastChunkIndex,
    accumulatedDurationSeconds: state.accumulatedDurationSeconds,
    pausedTranscriptionDbId: state.pausedTranscriptionDbId,
    visitPatientNameLocked: state.visitPatientNameLocked,
    draftPatientId: state.draftPatientId,
    nextCheckpointBatchNumber: state.nextCheckpointBatchNumber,
    lastUploadedChunkIndex: state.lastUploadedChunkIndex,
    recorderMimeType: state.recorderMimeType,
  });
}

export function writeRecordingDraftStarted(
  recordingId: string,
  recordingStartedAt: number,
  recorderMimeType?: string
): void {
  writeActiveManifest({
    recordingId,
    recordingStartedAt,
    lastUploadAt: recordingStartedAt,
    lastChunkIndex: -1,
    nextCheckpointBatchNumber: 1,
    recorderMimeType: recorderMimeType || undefined,
  });
}

/**
 * Persist one ~15s slice as a {@link Blob} in IndexedDB under `[recordingId, chunkIndex]`, then extend the
 * manifest (so a manifest entry always implies the slice is durably stored).
 */
export function saveRecordingChunkLocal(
  recordingId: string,
  chunkIndex: number,
  mimeType: string,
  blob: Blob
): Promise<void> {
  const writeManifestAfterChunk = (): void => {
    const prev = readRecordingCheckpointById(recordingId);
    const same = prev;
    const base: RecordingCheckpointState = {
      recordingId,
      recordingStartedAt: same?.recordingStartedAt ? same.recordingStartedAt : Date.now(),
      lastUploadAt: Date.now(),
      lastChunkIndex: Math.max(chunkIndex, same ? same.lastChunkIndex : -1),
      accumulatedDurationSeconds: same?.accumulatedDurationSeconds,
      pausedTranscriptionDbId: same?.pausedTranscriptionDbId,
      visitPatientNameLocked: same?.visitPatientNameLocked,
      draftPatientId: same?.draftPatientId,
      nextCheckpointBatchNumber: same?.nextCheckpointBatchNumber ?? 1,
      lastUploadedChunkIndex: same?.lastUploadedChunkIndex,
      recorderMimeType: same?.recorderMimeType ?? (mimeType || undefined),
    };
    writeManifest(base);
  };

  return ensureBackend().then((b) => {
    if (b === 'localstorage') {
      return saveChunkLocalStorage(recordingId, chunkIndex, mimeType, blob)
        .then(writeManifestAfterChunk)
        .catch((err) => {
          throw new Error(`localStorage quota exceeded saving recording chunk: ${err}`);
        });
    }
    return enqueue(async () => {
      const data = await blobToArrayBuffer(blob);
      await putChunkRecord({
        id: chunkRecordId(recordingId, chunkIndex),
        recordingId,
        chunkIndex,
        mimeType,
        data,
      });
      writeManifestAfterChunk();
    });
  });
}

/** Load one persisted slice for checkpoint stitch when the in-memory buffer no longer holds it. */
export function loadRecordingChunkLocal(recordingId: string, chunkIndex: number): Promise<Blob | null> {
  return ensureBackend().then((b) => {
    if (b === 'localstorage') {
      return loadChunkLocalStorage(recordingId, chunkIndex);
    }
    return enqueue(() => getChunkRecord(recordingId, chunkIndex))
      .then((rec) => {
        if (!rec?.data) {
          return null;
        }
        const mime = rec.mimeType || 'audio/webm;codecs=opus';
        return new Blob([rec.data], { type: mime });
      })
      .catch(() => null);
  });
}

/**
 * After a successful S3 checkpoint POST: persist {@link RecordingCheckpointState#nextCheckpointBatchNumber}
 * so the next stitch uses {@code chunks-{n}.{ext}} without colliding after partial batches or pause/resume.
 */
export function writeCheckpointNextBatchNumber(recordingId: string, nextBatchNumber: number): void {
  const prev = readRecordingCheckpointById(recordingId);
  if (!prev) {
    return;
  }
  writeManifest({
    ...prev,
    lastUploadAt: Date.now(),
    nextCheckpointBatchNumber: Math.max(1, Math.floor(nextBatchNumber)),
  });
}

/** After a successful checkpoint POST: track S3 progress separately from local {@link #lastChunkIndex}. */
export function writeCheckpointUploadProgress(
  recordingId: string,
  lastUploadedChunkIndex: number,
  nextBatchNumber: number
): void {
  const prev = readRecordingCheckpointById(recordingId);
  if (!prev) {
    return;
  }
  writeManifest({
    ...prev,
    lastUploadAt: Date.now(),
    lastUploadedChunkIndex: Math.max(prev.lastUploadedChunkIndex ?? -1, lastUploadedChunkIndex),
    nextCheckpointBatchNumber: Math.max(1, Math.floor(nextBatchNumber)),
  });
}

/** Merge pause/resume metadata into the active manifest (same recordingId). */
export function mergePauseVisitIntoManifest(
  recordingId: string,
  fields: Partial<
    Pick<
      RecordingCheckpointState,
      | 'accumulatedDurationSeconds'
      | 'pausedTranscriptionDbId'
      | 'visitPatientNameLocked'
      | 'draftPatientId'
      | 'nextCheckpointBatchNumber'
    >
  >
): void {
  const prev = readRecordingCheckpointById(recordingId);
  if (!prev) {
    return;
  }
  writeManifest({
    ...prev,
    ...fields,
  });
}
